import { Fragment, type ReactNode, useCallback, useEffect, useMemo } from 'react'

import messagesDanish from 'locales/da.json'
import { IntlProvider } from 'react-intl'

import {
	MediaQueryProvider,
	NoOpErrorBoundary,
	RouterProvider,
	renderDefaultSnackbar,
	SnackbarProvider,
	ThemeProvider,
	useStore,
} from '@subaio/components'
import { type Api, logError, type Page, type Router } from '@subaio/utils'
import { defaultBreakpointQueries } from '@subaio/webapp'
import { ContainerStatus, initializeContainer, invalidate } from '@subaio/webapp/src/containers'

import type { User } from 'apiTypes/User'

import type { Container } from 'lib/container'
import { getContainer } from 'lib/getContainer'
import { intlFormats } from 'lib/intlFormats'
import type { Store } from 'lib/store'

import { GlobalStyle, getTheme } from 'styles'

import { Error } from 'components/Error'
import { ModalProvider } from 'components/Modal'
import { Page as PageComponent } from 'components/Page'

type Props = {
	api: Api
	store: Store
	router: Router
	containers: Record<string, Container>

	setUser: (options?: User) => void
}

export const App = ({ api, store, router, containers, setUser }: Props) => {
	const { page, status, data, userAuth } = useStore(store)

	const container = getContainer(containers, page, userAuth)

	const setData = useCallback(
		(update: Record<string, any>) => {
			const { data, page: currentPage } = store.getState()
			// Abort state updates if page has changed
			if (page !== currentPage) return
			store.set({ data: { ...data, ...update } })
		},
		[store, page],
	)

	const updatePage = useCallback(
		(page: Page) => {
			store.set({ page, status: ContainerStatus.Initializing, data: {} })
		},
		[store],
	)

	const invalidateSelf = useCallback(
		(...invalidDependencies: Array<string>) =>
			invalidate(
				store,
				containers,
				{ query: page.query, updatePage, router, setUser, api, userAuth },
				async () => undefined,
				invalidDependencies,
			),
		[store, containers, page.query, updatePage, router, setUser, api, userAuth],
	)

	const title = useMemo(() => {
		if (!container?.title) return undefined

		if (typeof container.title !== 'function') return container.title

		if (status !== ContainerStatus.Running) return undefined

		return container.title({ ...page.params, ...data }) as ReactNode
	}, [container, data, page, status])

	const theme = useMemo(
		() => getTheme({ brandId: userAuth.status === 'authenticated' ? userAuth.brandId : undefined }),
		[userAuth],
	)

	useEffect(() => {
		if (typeof title !== 'string') return

		const original = document.title
		document.title = `${title} • ${original}`

		return () => {
			document.title = original
		}
	}, [title])

	useEffect(() => {
		if (status !== ContainerStatus.Initializing) return

		switch (container.authentication.type) {
			case 'public': {
				initializeContainer(
					store,
					container,
					{ query: page.query, updatePage, router, setUser, api, userAuth },
					async () => undefined,
				)
				break
			}
			case 'private': {
				initializeContainer(
					store,
					container,
					{ query: page.query, updatePage, router, setUser, api, userAuth },
					async () => undefined,
				)
				break
			}
		}
	}, [store, container, status, updatePage, page, router, setUser, api, userAuth])

	const props = {
		...page.params,
		api,
		userAuth,
		query: page.query,
		invalidateSelf,
		setData,
		setUser,
	}

	return (
		<ThemeProvider theme={theme}>
			<GlobalStyle />
			<IntlProvider
				locale='da-DK'
				defaultLocale='en'
				formats={intlFormats}
				defaultFormats={intlFormats}
				messages={messagesDanish}
			>
				<RouterProvider router={router} page={page} onPageChange={updatePage}>
					<MediaQueryProvider queries={defaultBreakpointQueries}>
						<ModalProvider mode='fixed'>
							<PageComponent>
								<SnackbarProvider render={renderDefaultSnackbar}>
									<NoOpErrorBoundary
										onError={(error, info) => {
											logError(error, info)

											store.set({ status: ContainerStatus.Errored })
										}}
									>
										<Fragment key={page.name}>
											{status === ContainerStatus.Running && <container.Running {...data} {...props} />}
											{status === ContainerStatus.Loading && <container.Loading {...data} {...props} />}
											{status === ContainerStatus.Initializing && (
												<container.Loading {...container.defaults} {...props} />
											)}
											{status === ContainerStatus.Errored && <Error />}
										</Fragment>
									</NoOpErrorBoundary>
								</SnackbarProvider>
							</PageComponent>
						</ModalProvider>
					</MediaQueryProvider>
				</RouterProvider>
			</IntlProvider>
		</ThemeProvider>
	)
}
